import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Calculator, IndianRupee, Landmark, ListChecks } from "lucide-react";
import { groupedCalculators } from "../calculators/categories";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

/**
 * /app/calculators — dedicated destination for all interactive calculators + a link into
 * the persisted "My Scenarios" view. Same content the dashboard already surfaces, but
 * standalone so the top-nav "Calculators" link lands somewhere focused.
 */
export function CalculatorsPage() {
  const { groups, total } = useMemo(() => {
    const g = groupedCalculators();
    return { groups: g, total: g.reduce((s, x) => s + x.items.length, 0) };
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="calculators-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-serif text-3xl text-text">
            <Calculator className="h-6 w-6 text-gold" /> Calculators
          </h1>
          <p className="mt-1 text-sm text-muted">
            {total} interactive tools grouped by what you&apos;re planning for. Save any run to your
            scenarios for later.
          </p>
        </div>
        <Link
          to="/my-scenarios"
          data-testid="calculators-my-scenarios-cta"
          className="inline-flex items-center gap-1.5 rounded-md border border-gold/40 bg-gold/5 px-3 py-1.5 text-xs font-medium text-gold hover:border-gold hover:bg-gold/10"
        >
          <ListChecks className="h-3.5 w-3.5" /> My Scenarios
        </Link>
      </div>

      <nav className="mt-5 flex flex-wrap gap-2" data-testid="calculator-category-nav">
        {groups.map((group) => (
          <a
            key={group.id}
            href={`#${slug(group.id)}`}
            className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted transition-colors hover:border-gold hover:text-text"
          >
            {group.id}
            <span className="ml-1.5 text-muted/70">{group.items.length}</span>
          </a>
        ))}
      </nav>

      <div className="mt-8 flex flex-col gap-10">
        {groups.map((group) => (
          <section key={group.id} id={slug(group.id)} className="scroll-mt-24">
            <div className="flex items-center gap-3 border-b border-border pb-2">
              <Landmark className="h-4 w-4 text-gold" />
              <div>
                <h3 className="text-lg font-semibold text-text">{group.id}</h3>
                <p className="text-sm text-muted">{group.blurb}</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {group.items.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  data-testid={`calc-page-${item.key}`}
                  className="group rounded-lg border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[0_10px_30px_-15px_rgba(212,175,55,0.4)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-text">{item.title}</div>
                      <div className="mt-1 text-xs text-muted line-clamp-2">{item.description}</div>
                    </div>
                    <IndianRupee className="h-4 w-4 text-gold/60 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default CalculatorsPage;
